import { inject, Injectable } from "@angular/core";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { BehaviorSubject, combineLatest, Observable, switchMap } from "rxjs";
import { Tradable } from "@models/interfaces/tradable";
import { filter, map, take } from "rxjs/operators";
import {
	filterLeveragedType,
	filterSortTradables,
	filterTradablesByCondition,
	filterTradablesByPill,
	filterTradablesBySearch,
	filterTradablesPriceOrder,
	filterTradableUKUsers,
	getBuyAssetListFilters
} from "@modules/trade/helpers/helper";
import { BpOption } from "@models/ui-components/bp-option";
import { ASSET_LIST_FILTERS } from "@modules/trade/constants/constants";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import {
	API_ETC_TYPE,
	API_ETF_TYPE,
	API_LEVERAGED_TOKEN_TYPE,
	API_METAL_TYPE,
	API_SECURITY_TYPE,
	API_STOCK_TYPE,
	PRICES_SCROLL_PER_PAGE,
	STEELCOIN_INTERNAL_ID
} from "@helpers/constants";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { MtTypeSwitcherOption } from "@modules/margin-trading/components/mt-type-switcher/mt-type-switcher.component";
import {
	AssetOrderType,
	AssetSort,
	SortOrder,
	TimePeriod
} from "../../../../api/graphql/bff/generated/types.graphql-gen";
import { AssetFragment } from "../../../../api/graphql/bff/generated/fragments.service.graphql-gen";
import { MarginTradingRemote } from "@modules/margin-trading/services/margin-trading.remote";

interface PillsBuyListModel {
	allPills: BpOption[];
	selectedPill: BpOption;
}

export interface BuyAssetListModel {
	pills: PillsBuyListModel;
	sortingName: Sort;
	sortingPrice: Sort;
	sortingChange: Sort;
	searchValue: string;
	pageSize: number;
	leverageType: MtTypeSwitcherOption;
}

const initialState: BuyAssetListModel = {
	pills: {
		allPills: ASSET_LIST_FILTERS,
		selectedPill: ASSET_LIST_FILTERS[0]
	},
	sortingName: {
		active: "name",
		direction: ""
	},
	sortingPrice: {
		active: "price",
		direction: ""
	},
	sortingChange: {
		active: "change",
		direction: ""
	},
	searchValue: "",
	pageSize: PRICES_SCROLL_PER_PAGE,
	leverageType: MtTypeSwitcherOption.MARGIN
};

interface MarginTrading {
	leverageType: MtTypeSwitcherOption;
	isMarginSelected: boolean;
	showDropdown: boolean;
	hasNextPage: boolean;
	after: string;
}

export interface BuyListModel {
	tradables: Tradable[];
	pills: PillsBuyListModel;
	showBuyAgain: boolean;
	isSearching: boolean;
	isPriceOrder: boolean;
	marginTrading: MarginTrading;
}

interface MarginAssets {
	assets: Extract<
		AssetFragment,
		{
			__typename?: "CryptoAsset";
		}
	>[];
	hasNextPage: boolean;
	after: string;
}

const initialMarginAssets: MarginAssets = {
	assets: [],
	hasNextPage: false,
	after: ""
};

@Injectable()
export class BuyAssetListFacade {
	private readonly tradableListService = inject(TradableListService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly statsigService = inject(StatsigClientService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly bff: MarginTradingRemote = inject(MarginTradingRemote);

	private buyAssetListModel$$ = new BehaviorSubject<BuyAssetListModel>(initialState);
	public readonly buyAssetListModel$ = this.buyAssetListModel$$.asObservable();

	public marginAssets$$ = new BehaviorSubject<MarginAssets>(initialMarginAssets);
	public readonly marginAssets$ = this.marginAssets$$.asObservable();

	private readonly areLeverageActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE);
	private readonly areSecurityTokenActive$: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.SECURITY_TOKENS
	);
	private readonly hideSteelCoin$ = this.statsigService.isFeatureActive(FEAT_GATES.REMOVE_STEELCOIN);
	private readonly isMarginTradingEnabled$: Observable<boolean> = this.statsigService.isFeatureActive(
		FEAT_GATES.MARGIN_TRADING_ENABLED
	);
	private readonly isMarginTradingBuyEnabled$: Observable<boolean> = this.statsigService.isFeatureActive(
		FEAT_GATES.MARGIN_TRADING_BUY
	);

	getMarginAssets$(
		after: string = null,
		sortBy: AssetSort = AssetSort.MarketCap,
		sortOrder: SortOrder = SortOrder.Desc
	): Observable<MarginAssets> {
		return combineLatest({
			mtEnabled: this.isMarginTradingEnabled$,
			mtBuyEnabled: this.isMarginTradingBuyEnabled$
		}).pipe(
			filter(({ mtEnabled, mtBuyEnabled }) => mtEnabled && mtBuyEnabled),
			switchMap(() => this.getMarginAsssetsQuery(after, sortBy, sortOrder))
		);
	}

	private getMarginAsssetsQuery(
		after: string = null,
		sortBy: AssetSort = AssetSort.MarketCap,
		sortOrder: SortOrder = SortOrder.Desc
	): Observable<MarginAssets> {
		return this.bff
			.getAssetsFetch({
				input: {
					filters: {
						orderTypes: [AssetOrderType.MarginLong],
						settings: {
							includeHiddenFromDiscovery: false
						}
					},
					sortBy,
					sortOrder
				},
				after,
				period: TimePeriod.Day,
				includeOHLC: false,
				includeMaximumLeverage: true
			})
			.pipe(
				map((assets) => ({
					assets: assets.edges.map(
						(edge) =>
							edge.node as Extract<
								AssetFragment,
								{
									__typename?: "CryptoAsset";
								}
							>
					),
					hasNextPage: assets.pageInfo.hasNextPage,
					after: assets.pageInfo.endCursor || ""
				}))
			);
	}

	public viewModel$(): Observable<BuyListModel> {
		return combineLatest([
			this.tradableListService.getTradablesObservable$,
			this.activatedRoute.queryParams,
			this.areLeverageActive$,
			this.areSecurityTokenActive$,
			this.buyAssetListModel$,
			this.marginAssets$,
			this.isMarginTradingEnabled$,
			this.isMarginTradingBuyEnabled$,
			this.hideSteelCoin$
		]).pipe(
			map(
				([
					tradables,
					qp,
					leverageEnabled,
					securityTokenEnabled,
					buyListModel,
					marginAssets,
					isMarginTradingEnabled,
					isMarginTradingBuyEnabled,
					hideSteelCoin
				]) => {
					const { pills, searchValue, pageSize, leverageType } = buyListModel;
					// If Leverage and Margin Trading are Disabled remove the leverage pill
					if (!leverageEnabled && (!isMarginTradingEnabled || !isMarginTradingBuyEnabled)) {
						pills.allPills = pills.allPills.filter((f) => f.value !== API_LEVERAGED_TOKEN_TYPE);
					}

					// If Security Tokens are Disabled remove the security token pill
					if (!securityTokenEnabled || hideSteelCoin) {
						pills.allPills = pills.allPills.filter((f) => f.value !== API_SECURITY_TYPE);
					}

					// Remove the Stocks, ETFs and Metals  token pill
					pills.allPills = pills.allPills.filter(
						(f) =>
							f.value !== API_STOCK_TYPE &&
							f.value !== API_ETF_TYPE &&
							f.value !== API_METAL_TYPE &&
							f.value !== API_ETC_TYPE &&
							f.value !== API_SECURITY_TYPE
					);

					//filtered assets for UK users
					const filteredAssetsForUKUsers = filterTradableUKUsers(tradables);

					// If is Price Order, remove the tradables that are not available for price order
					const filterTradables = filterTradablesPriceOrder(qp.isPriceOrder, filteredAssetsForUKUsers);

					// Filter assets under certain conditions, is index only, is buy active, is FiatEarnAsset
					const filterTradableByConditions = hideSteelCoin
						? filterTradablesByCondition(filterTradables).filter(
								(tradable) => tradable.getId() !== STEELCOIN_INTERNAL_ID
							)
						: filterTradablesByCondition(filterTradables);

					// If the user is searching for a tradable filter out what is not matching the search
					const isSearching = searchValue.length > 0;
					//If Security Tokens are Disabled filterout search results
					//TODO: remove this once FF is removed
					let searchTradables = [];
					if (!securityTokenEnabled) {
						searchTradables = filterTradablesBySearch(searchValue, filterTradableByConditions).filter(
							(f) => !(f instanceof SecurityToken)
						);
					} else {
						searchTradables = filterTradablesBySearch(searchValue, filterTradableByConditions);
					}

					// Select the tradables that are under the selected pill
					const pillTradables = filterTradablesByPill(
						pills.selectedPill,
						isSearching ? searchTradables : filterTradableByConditions
					);

					// Check if the asset type to display is Margin Leverage
					const isMarginSelected =
						isMarginTradingEnabled &&
						isMarginTradingBuyEnabled &&
						pills.selectedPill.value === API_LEVERAGED_TOKEN_TYPE &&
						leverageType === MtTypeSwitcherOption.MARGIN;

					// Select correct Leveraged type tradable
					const leverageTradables = filterLeveragedType(marginAssets.assets, tradables, searchValue);

					// Check the sorting state and sort the tradables accordingly
					const sortTradables = filterSortTradables(
						buyListModel,
						isMarginSelected ? leverageTradables : pillTradables,
						pills.selectedPill
					);

					// Slice the tradables to the page size
					const sliceTradables = isMarginSelected ? sortTradables : sortTradables.slice(0, pageSize);

					return {
						tradables: sliceTradables,
						pills: {
							allPills: qp.isPriceOrder
								? getBuyAssetListFilters(pills.allPills, tradables)
								: pills.allPills,
							selectedPill: pills.selectedPill
						},
						showBuyAgain: pills.selectedPill.value === "" && !isSearching && !qp.isPriceOrder,
						isSearching,
						isPriceOrder: qp.isPriceOrder,
						marginTrading: {
							showDropdown:
								leverageEnabled &&
								isMarginTradingBuyEnabled &&
								isMarginTradingEnabled &&
								pills.selectedPill.value === API_LEVERAGED_TOKEN_TYPE,
							isMarginSelected,
							leverageType,
							hasNextPage: marginAssets.hasNextPage,
							after: marginAssets.after
						}
					};
				}
			)
		);
	}

	public setPill(pill: BpOption): void {
		const currentState = this.buyAssetListModel$$.getValue();
		this.buyAssetListModel$$.next({
			...currentState,
			pageSize: initialState.pageSize,
			pills: {
				...currentState.pills,
				selectedPill: pill
			},
			leverageType:
				pill.value !== API_LEVERAGED_TOKEN_TYPE ? MtTypeSwitcherOption.MARGIN : currentState.leverageType
		});
	}

	public setSorting(sortState: Sort): void {
		const currentState = this.buyAssetListModel$$.getValue();
		this.buyAssetListModel$$.next({
			...currentState,
			pageSize: initialState.pageSize,
			sortingName: sortState.active === "name" ? sortState : initialState.sortingName,
			sortingPrice: sortState.active === "price" ? sortState : initialState.sortingPrice,
			sortingChange: sortState.active === "change" ? sortState : initialState.sortingChange
		});
	}

	public setSearching(searchValue: string): void {
		const currentState = this.buyAssetListModel$$.getValue();
		this.buyAssetListModel$$.next({
			...currentState,
			pageSize: initialState.pageSize,
			searchValue
		});
	}

	public setPageSize(isMarginSelected: boolean, hasNextPage: boolean, after: string): void {
		if (isMarginSelected && !hasNextPage) {
			return;
		}

		if (isMarginSelected && hasNextPage) {
			const currentMarginAssets = this.marginAssets$$.getValue();
			this.getMarginAssets$(after)
				.pipe(take(1))
				.subscribe((res) =>
					this.marginAssets$$.next({
						assets: [...res.assets, ...currentMarginAssets.assets],
						hasNextPage: res.hasNextPage,
						after: res.after
					})
				);
			return;
		}

		const currentState = this.buyAssetListModel$$.getValue();
		this.buyAssetListModel$$.next({
			...currentState,
			pageSize: currentState.pageSize + initialState.pageSize
		});
	}

	public setLeverageType(type: MtTypeSwitcherOption): void {
		const currentState = this.buyAssetListModel$$.getValue();
		this.buyAssetListModel$$.next({
			...currentState,
			pageSize: initialState.pageSize,
			leverageType: type
		});
	}
}
